import { useEffect, useState, useMemo, useCallback } from 'react'
import ColumnHeader from './ColumnHeader'
import { packFilter } from './Filter'
import { ScoopSpinner, ScoopTable, ScoopText, ScoopColumn } from '../../../../../ui'
import styles from './DefineInputQuery.module.scss'

export default function DefineInputQuery({
    editingAnchorEl,
    setEditingAnchorEl,
    selectedColumns,
    selectedFilters,
    handleFilterChange,
    setSelectedColumns,
    index,
    table,
    inputQuery,
    server,
    worksheetID,
    aggregate,
    timeLevel,
    useLatest,
    groupByDate,
    pickedDate,
    save,
    setSave,
    dataRetrieval,
    continueToNext,
    setIsCreating,
    resetState,
}) {
    const [columns, setColumns] = useState(() => {
        if (selectedColumns?.index === index) {
            return selectedColumns?.columns?.map((col) => col.columnName) || []
        } else {
            return []
        }
    })

    const [filterList, setFilterList] = useState(() => {
        if (selectedFilters?.index === index) {
            if (
                selectedFilters?.filter?.length > 0 &&
                selectedFilters?.filter[0]?.attributeName !== null
            ) {
                return selectedFilters?.filter
            } else {
                return []
            }
        } else {
            return []
        }
    })

    const [previewRows, setPreviewRows] = useState([])
    const [baseColumns, setBaseColumns] = useState([])

    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(false)
    const [anchorEl, setAnchorEl] = useState(editingAnchorEl)

    useEffect(() => {
        setAnchorEl(editingAnchorEl)
    }, [editingAnchorEl?.anchorEl])

    // Memoized callback for updating blending preview
    const updateBlendingPreview = useCallback(async () => {
        setEditingAnchorEl(null)
        setIsLoading(true)
        try {
            await server.sheetPostData(
                {
                    action: 'addOn',
                    addOnAction: 'preview',
                    inputQuery: inputQuery,
                    numRows: 15,
                    sheetID: worksheetID,
                },
                processPreviewBlending
            )
        } catch (e) {
            console.log(e)
        } finally {
            setIsLoading(false)
        }
    }, [server, inputQuery, worksheetID, setEditingAnchorEl])

    // Compute final columns with headerRender using current state values
    const tableColumns = useMemo(() => {
        return baseColumns.map((col) => ({
            ...col,
            headerRender: ({ column }) => (
                <ColumnHeader
                    editingAnchorEl={anchorEl}
                    sheetID={worksheetID}
                    headerName={column.headerName}
                    inputQuery={inputQuery}
                    isChecked={columns.includes(column.headerName)}
                    setColumns={setColumns}
                    setCurrentColumns={setSelectedColumns}
                    filterList={filterList || []}
                    setFilterList={setFilterList}
                    server={server}
                    updatePreviewData={updateBlendingPreview}
                />
            ),
        }))
    }, [
        baseColumns,
        anchorEl,
        worksheetID,
        inputQuery,
        columns,
        setSelectedColumns,
        filterList,
        server,
        updateBlendingPreview,
    ])

    useEffect(() => {
        if (!filterList) return
        handleFilterChange(filterList)
    }, [filterList])

    useEffect(() => {
        if (selectedFilters?.index === index) {
            setFilterList(selectedFilters?.filter)
        }
    }, [selectedFilters.filter])

    useEffect(() => {
        setColumns(selectedColumns?.columns.map((col) => col.columnName))
    }, [selectedColumns.columns])

    useEffect(() => {
        async function updateBlending() {
            await updateBlendingPreview()
        }

        updateBlending()
    }, [
        table,
        timeLevel,
        useLatest,
        groupByDate,
        aggregate,
        pickedDate,
        dataRetrieval,
        selectedFilters.filter,
    ])

    useEffect(() => {
        async function saveData() {
            if (save) {
                await handleSaveBlending()
            }
        }

        saveData()
    }, [save])

    function transform(cType, val) {
        if (cType) {
            if (cType === 'DateTime') {
                return new Date(val)
            }
            return val
        }
        return val
    }

    const processPreviewBlending = (previewData) => {
        setIsLoading(true)
        if (!previewData || !previewData.rows || !previewData.rows.length > 0) {
            setError(true)
            return
        }
        var newBaseColumns = []
        var newPreviewRows = []

        for (var cnum = 0; cnum < previewData.rows[0].length - 1; cnum++) {
            var minWidth = 8
            var cName = previewData.rows[0][cnum]
            if (cnum < table.columns.length) {
                if (table.columns[cnum].columnType !== 'String') {
                    minWidth = 15
                } else {
                    for (var rnum = 1; rnum < previewData.rows.length; rnum++) {
                        if (previewData.rows[rnum][cnum].length > minWidth) {
                            minWidth = previewData.rows[rnum][cnum].length
                        }
                    }
                }
                if (
                    table.columns[cnum].columnType === 'String' &&
                    table.columns[cnum].columnName.length > minWidth
                ) {
                    minWidth = table.columns[cnum].columnName.length
                }
                cName = table.columns[cnum].columnName
            } else {
                minWidth = 20
            }
            if (minWidth > 35) {
                minWidth = 35
            }

            // Base column config (headerRender is added via useMemo)
            newBaseColumns.push({
                field: cName,
                headerName: cName,
                width: minWidth * 11,
                sortable: false,
            })
        }

        for (var rnum = 1; rnum < previewData.rows.length; rnum++) {
            var newRow = { id: rnum }
            for (cnum = 0; cnum < previewData.rows[0].length; cnum++) {
                var x = previewData.rows[rnum][cnum]
                if (cnum < table.columns.length) {
                    newRow[table.columns[cnum].columnName] = transform(
                        table.columns[cnum].columnType,
                        x
                    )
                } else {
                    newRow[previewData.rows[0][cnum]] = previewData.rows[rnum][cnum]
                }
            }
            newPreviewRows.push(newRow)
        }
        setIsLoading(false)
        setError(false)
        setBaseColumns(newBaseColumns)
        setPreviewRows(newPreviewRows)
    }

    const finishSaveBlending = () => {
        setIsCreating(false)
        continueToNext()
        resetState()
        setSave(false)
    }

    const handleSaveBlending = async () => {
        setIsCreating(true)
        inputQuery.filter = packFilter(filterList)
        try {
            await server.sheetPostData(
                {
                    action: 'addOn',
                    addOnAction: 'saveInputQuery',
                    inputQuery: inputQuery,
                    index: index,
                    refresh: true,
                    sheetID: worksheetID,
                },
                finishSaveBlending
            )
        } catch (e) {
            setIsCreating(false)
            continueToNext()
        } finally {
            resetState()
        }
    }

    return (
        <div>
            <ScoopColumn className={styles.container}>
                {/* Data table with integrated column selection in headers */}
                <div className={styles.tableContainer}>
                    {!isLoading && !error && tableColumns?.length > 0 && (
                        <ScoopTable
                            columns={tableColumns}
                            rows={previewRows}
                            resizable={false}
                            reorderable={true}
                            rowHeight={32}
                            headerHeight={48}
                            domLayout="normal"
                            height="100%"
                        />
                    )}
                    {isLoading && (
                        <div className={styles.loadingContainer}>
                            <ScoopSpinner size={72} />
                        </div>
                    )}
                    {!isLoading && error && (
                        <div className={styles.errorContainer}>
                            <ScoopText className={styles.errorText}>No data available</ScoopText>
                        </div>
                    )}
                </div>
            </ScoopColumn>
        </div>
    )
}
